/**
 * Thai line-breaking units.
 *
 * Thai has no spaces between words, so wrapping uses Intl.Segmenter word boundaries,
 * plus two user controls:
 *  - a no-break word list: a boundary falling inside one of these words is removed
 *  - manual marks typed into the text: JOIN glues its neighbours, BRK allows a break
 */
export const JOIN = '⁠'; // WORD JOINER: "do not break here"
export const BRK = '​';  // ZERO WIDTH SPACE: "may break here"
export const MARKS_RE = /[​⁠]/g;

export type UnitKind = 'text' | 'space' | 'brk';
export interface Unit { s: string; k: UnitKind }

interface Token { s: string; i: number; k: 'text' | 'space' }

export function createSegmenters(): { word: Intl.Segmenter; grapheme: Intl.Segmenter } | null {
  if (typeof Intl === 'undefined' || typeof Intl.Segmenter !== 'function') return null;
  return {
    word: new Intl.Segmenter('th', { granularity: 'word' }),
    grapheme: new Intl.Segmenter('th', { granularity: 'grapheme' })
  };
}

export function graphemes(s: string, seg: Intl.Segmenter): string[] {
  const out: string[] = [];
  for (const x of seg.segment(s)) out.push(x.segment);
  return out;
}

/** Word tokens of a chunk without manual marks; boundaries inside a no-break word are merged away. */
export function chunkTokens(str: string, dict: readonly string[], seg: Intl.Segmenter): Token[] {
  const toks: Token[] = [];
  for (const x of seg.segment(str)) toks.push({ s: x.segment, i: x.index, k: /^\s+$/.test(x.segment) ? 'space' : 'text' });
  if (!dict.length || toks.length < 2) return toks;

  const glue = new Set<number>();
  for (const w of dict) {
    if (!w) continue;
    let p = str.indexOf(w);
    while (p !== -1) {
      for (let k = 1; k < toks.length; k++) if (toks[k].i > p && toks[k].i < p + w.length) glue.add(k);
      p = str.indexOf(w, p + 1);
    }
  }
  if (!glue.size) return toks;

  const out: Token[] = [{ ...toks[0] }];
  for (let k = 1; k < toks.length; k++) {
    if (glue.has(k)) { const prev = out[out.length - 1]; prev.s += toks[k].s; prev.k = 'text'; }
    else out.push({ ...toks[k] });
  }
  return out;
}

/** Split one paragraph line into wrap units. Concatenating every unit's text gives back the line exactly. */
export function lineUnits(line: string, dict: readonly string[], seg: Intl.Segmenter): Unit[] {
  const raw: Array<{ s: string; k: UnitKind | 'join' }> = [];
  for (const part of line.split(/([​⁠])/)) {
    if (!part) continue;
    if (part === JOIN) raw.push({ s: part, k: 'join' });
    else if (part === BRK) raw.push({ s: part, k: 'brk' });
    else raw.push(...chunkTokens(part, dict, seg));
  }

  const units: Unit[] = [];
  for (let k = 0; k < raw.length; k++) {
    const t = raw[k];
    if (t.k === 'join') {
      const prev = units[units.length - 1];
      const next = raw[k + 1];
      if (prev && prev.k !== 'brk') {
        prev.s += t.s; prev.k = 'text';
        if (next && next.k === 'text') { prev.s += next.s; k++; }
      } else units.push({ s: t.s, k: 'text' });
      continue;
    }
    units.push({ s: t.s, k: t.k });
  }
  return units;
}
